import { FlyOutDimensionWrapper } from '../../FlyOutProductDimensions/FlyOutDimensionWrapper/FlyOutDimensionWrapper'
import { FormLockedType } from '../../../types/FormLockedType'
import { FieldArray, FormikProps, useFormikContext } from 'formik'
import {
  FlyoutProductSections,
  ContractDimension,
  FlyoutProductAddEditSectionName,
} from '../../../types/addEditProduct'
import { get, isEmpty } from 'lodash'
import { useStyles } from './ContractDimensions.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUpRightFromSquare,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { Grid, Typography } from '@mui/material'
import {
  ButtonV2,
  CheckBoxFieldv2,
  LabraToolTipV2,
  NoteV2,
  TextInputV2,
} from '@labrav/react-components'

import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  getRandom,
  getUnique12Number,
} from '../../../common/utils/helperFunctions'
import { Link } from 'react-router-dom'
import {
  contractDimesnionDisplayNameUrl,
  dimensionNameRegex,
  giveNumberInputValue,
  notEditableContractDimensionsCheckboxTooltip,
} from '../../../common/utils/productCreationUtil'
import { AwsDimensionCategories } from '../../AddEditProduct/AddEditProductListing/FlyoutProductDimensionsSection/FlyoutProductDimensionsSection'
import { useFlag } from '@labrav/flags'
import { FlyoutProductDimensionsContractDimensionsFaqs } from './FlyoutProductDimensionsContractDimensionsFaqs'
import { getListingTextHighlights } from '../../../common/utils/listingTextRestrictions'

export interface ContractDimensions {
  requirementsLink: string
  awsDimensionCategories: AwsDimensionCategories
  detailView?: boolean
  detailViewTooltip?: string
  productView?: boolean
}

const emptyContractDimension = {
  name: '',
  label: '',
  price: null,
  price1m: null,
  price12m: null,
  price24m: null,
  price36m: null,
  additionalUsagePrice: null,
  subscriptionPrice: null,
  labelDescription: '',
  isAdditionalUsageAllowed: false,
  checkBoxAdditionalUsageAllowed: false,
  additionalUsageDescription: null,
}

export const ContractDimensions = ({
  isFormLocked,
  awsDimensionCategories,
  detailView,
  detailViewTooltip,
  productView,
}: ContractDimensions & FormLockedType) => {
  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<FlyoutProductSections>

  const contractDimensions = values.productDimensions.dimensions
  const dimensionsTouched = touched.productDimensions
  const productDimensions = values.productDimensions
  const { classes } = useStyles()

  const { flag: isFreeTrialProductEnable } = useFlag('freeTrialInProduct')
  const { flag: listingValidation } = useFlag('listingValidation')

  const dimensionLength = contractDimensions?.length
  const productDimensionsErrors = errors.productDimensions

  const { usageMap } = awsDimensionCategories
  const dimensionCategoryName = values.productDimensions.dimensionCategoryName
  const usageBasedUnitType =
    Object.keys(usageMap).length > 0 ? usageMap[dimensionCategoryName] : ''

  const randomKey = useMemo(() => getRandom(), [dimensionLength])
  const randomKeyCheckBox = useMemo(() => getRandom(), [contractDimensions])
  const additionalDimentions =
    values.productDimensions.additionalDimensions || []

  const isAdditionalDimentionsEmpty = !additionalDimentions?.length

  useEffect(() => {
    setFieldValue(
      'productDimensions.dimensions',
      contractDimensions.map(dimension => ({
        ...dimension,
        dimensionUnit: dimensionCategoryName,
      }))
    )
  }, [dimensionCategoryName])
  const handleCheckboxChange = async (
    checked: boolean,
    index: number,
    dimension: ContractDimension
  ) => {
    await setFieldValue(
      `productDimensions.dimensions.${index}.checkBoxAdditionalUsageAllowed`,
      checked
    )
    if (checked) {
      const newDimension = {
        ...emptyContractDimension,
        name: dimension.name,
        label: dimension.label,
        isAdditionalUsageAllowed: true,
        checkBoxAdditionalUsageAllowed: true,
        dimensionUnit: usageBasedUnitType,
      }
      const newAdditionalDimentions = [...additionalDimentions, newDimension]
      await setFieldValue(
        'productDimensions.additionalDimensions',
        newAdditionalDimentions
      )
    } else {
      const updatedAdditionalDimensions = additionalDimentions.filter(
        additionalDimension => additionalDimension.name !== dimension.name
      )
      await setFieldValue(
        'productDimensions.additionalDimensions',
        updatedAdditionalDimensions
      )
    }
  }

  const getIsError = (path: string) => {
    return !!(
      get(dimensionsTouched, path, false) &&
      get(productDimensionsErrors, path, false)
    )
  }
  const getErrorText = (path: string) => {
    return (
      (!!get(dimensionsTouched, path, false) &&
        get(productDimensionsErrors, path, '')) ||
      ''
    )
  }

  const handleChangeDescription = async (
    event: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = event.target.value as string
    setFieldValue(
      `productDimensions.dimensions.${index}.labelDescription`,
      value
    )
    if (
      isFreeTrialProductEnable?.value &&
      values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]?.freeTrial
    ) {
      const updatedDimensions = [...contractDimensions]
      const updatedFreeTrialDimensions =
        values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]?.freeTrial
          ?.dimensions || []
      const updateFreeTrialIndex = updatedFreeTrialDimensions?.findIndex(
        item => item?.name === updatedDimensions[index].name
      )
      updatedFreeTrialDimensions[updateFreeTrialIndex] = {
        ...updatedFreeTrialDimensions[updateFreeTrialIndex],
        labelDescription: value,
      }
      await setFieldValue(
        'productDimensions.freeTrial.dimensions',
        updatedFreeTrialDimensions
      )
    }
  }

  const getApiKeyName = (nameValue: string) => {
    return `${nameValue.replace(dimensionNameRegex, '_')?.substring(0, 20)}_${getUnique12Number()}`
  }

  const handleChangeDisplayName = async (
    event: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = event.target.value as string
    const updatedDimensions = [...contractDimensions]
    const updatedAdditionalDimentions = [...additionalDimentions]
    const isExisting = updatedDimensions[index]?.isExisting

    if (updatedDimensions[index].checkBoxAdditionalUsageAllowed) {
      const additionalDimensionIndex = updatedAdditionalDimentions.findIndex(
        additionalDimension =>
          additionalDimension.name === updatedDimensions[index].name
      )
      if (additionalDimensionIndex > -1) {
        updatedAdditionalDimentions[additionalDimensionIndex] = {
          ...updatedAdditionalDimentions[additionalDimensionIndex],
          ...(!isExisting ? { name: getApiKeyName(value) } : {}),
          label: value,
        }
      }

      await setFieldValue(
        'productDimensions.additionalDimensions',
        updatedAdditionalDimentions
      )
    }

    if (
      isFreeTrialProductEnable?.value &&
      values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]?.freeTrial
    ) {
      const updatedFreeTrialDimensions =
        values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]?.freeTrial
          ?.dimensions || []
      const updateFreeTrialIndex = updatedFreeTrialDimensions?.findIndex(
        item => item?.name === updatedDimensions[index].name
      )
      updatedFreeTrialDimensions[updateFreeTrialIndex] = {
        ...updatedFreeTrialDimensions[updateFreeTrialIndex],
        name: getApiKeyName(value),
        label: value,
      }
      await setFieldValue(
        'productDimensions.freeTrial.dimensions',
        updatedFreeTrialDimensions
      )
    }

    updatedDimensions[index] = {
      ...updatedDimensions[index],
      ...(!isExisting ? { name: getApiKeyName(value) } : {}),
      label: value,
    }

    await setFieldValue('productDimensions.dimensions', updatedDimensions)
  }

  const handleRemoveDimension = async (dimension: ContractDimension) => {
    const updatedAdditionalDimensions = additionalDimentions.filter(
      additionalDimension => additionalDimension.name !== dimension.name
    )
    if (updatedAdditionalDimensions.length !== additionalDimentions.length) {
      await setFieldValue(
        'productDimensions.additionalDimensions',
        updatedAdditionalDimensions
      )
    }

    if (
      isFreeTrialProductEnable?.value &&
      values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]?.freeTrial
    ) {
      await setFieldValue(
        'productDimensions.freeTrial.dimensions',
        values?.[
          FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS
        ]?.freeTrial?.dimensions?.filter(item => item?.name !== dimension?.name)
      )
    }
  }

  const handleChangePrice = async (
    e: React.ChangeEvent<HTMLInputElement>,
    keyName: string
  ) => {
    await setFieldValue(keyName, e.target.value === '' ? null : e.target.value)
  }

  const isDisable = !!isFormLocked || detailView
  const [indexModal, setIndexModal] = useState(0)

  return (
    <FlyOutDimensionWrapper
      heading="Contract dimensions"
      sectionText="For each contract dimension that you define, assign a buyer-friendly display name and description. Optionally associate an additional usage dimension per contract dimension by enabling pay-as-you-go pricing."
      secondaryActions={
        <FlyoutProductDimensionsContractDimensionsFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
      children={
        <>
          <NoteV2
            description={
              'Your listing will only reflect the prices added by you once it is Public. All dimension prices before that would be set to $0.01 for testing purposes.'
            }
          />

          <FieldArray name="productDimensions.dimensions">
            {({ push, remove }) => (
              <>
                {[...contractDimensions]?.map((dimension, index) => (
                  <div
                    className={classes.row}
                    data-testid="contract-row"
                    key={index}
                  >
                    <div className={classes.titleAndIcon}>
                      <div className={classes.title}>
                        Contract dimension {index + 1}
                      </div>
                      {dimensionLength > 1 && !dimension?.isExisting ? (
                        <div
                          className={clsx(
                            classes.icon,
                            detailView ? classes.disableTrash : ''
                          )}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            style={{ width: '18px', height: '20px' }}
                            data-testid="trash-contractDimensions"
                            onClick={async () => {
                              await remove(index)
                              await handleRemoveDimension(dimension)
                            }}
                          />
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>

                    <Grid container>
                      <Grid size={12} gap={0}>
                        <Typography
                          className={clsx(
                            classes.sectionHeading,
                            classes.headingWithDesc
                          )}
                        >
                          Display name
                        </Typography>
                        <Typography className={classes.headingDescription}>
                          This is the name of the plan of your product which you
                          are selling. For more information please refer to{' '}
                          <Link
                            target="_blank"
                            to={contractDimesnionDisplayNameUrl}
                            data-testid="cell-link"
                            className={classes.linkWrapper}
                          >
                            <>
                              this article{' '}
                              <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                                style={{
                                  width: '14px',
                                  height: '15px',
                                  marginLeft: '3px',
                                  marginBottom: '-1px',
                                  color: '#1942C5',
                                }}
                              />
                            </>
                          </Link>
                          .
                        </Typography>
                      </Grid>
                      <Grid
                        key={`contract-${randomKey}-label`}
                        className={classes.displayName}
                        size={6}
                      >
                        <LabraToolTipV2
                          followCursor={detailView}
                          tooltipPlacement="bottom-start"
                          description={detailViewTooltip}
                        >
                          <TextInputV2
                            keyName={`productDimensions.dimensions.${index}.label`}
                            title="Enter a unique display name"
                            type="text"
                            placeHolder="Enter a unique display name"
                            maxCharacters={80}
                            value={dimension.label}
                            updatedValue={dimension.label}
                            isError={getIsError(`dimensions.${[index]}.label`)}
                            onChange={event =>
                              handleChangeDisplayName(event, index)
                            }
                            isReadOnly={isFormLocked as boolean}
                            isRequired={false}
                            errorText={getErrorText(
                              `dimensions.${[index]}.label`
                            )}
                            showSpinner={false}
                            onBlur={handleBlur}
                            supportingText="Eg: Starter Plan or Silver Plan"
                            viewOnly={detailView}
                            highlights={
                              listingValidation?.value &&
                              getIsError(`dimensions.${[index]}.label`)
                                ? getListingTextHighlights(
                                    dimension.label,
                                    'plain'
                                  )
                                : []
                            }
                          />
                        </LabraToolTipV2>
                      </Grid>
                    </Grid>

                    <div style={{ width: '100%' }}>
                      <Typography
                        className={clsx(
                          classes.sectionHeading,
                          classes.sectionBottom
                        )}
                      >
                        Contract price (USD)
                      </Typography>
                      <LabraToolTipV2
                        followCursor={detailView}
                        tooltipPlacement="bottom-start"
                        description={detailViewTooltip}
                      >
                        <Grid container spacing={4}>
                          <Grid size={3}>
                            <div key={`contract-${randomKey}-price1m`}>
                              <TextInputV2
                                keyName={`productDimensions.dimensions.${index}.price1m`}
                                title="1 month price"
                                type="number"
                                placeHolder="1 month price"
                                value={giveNumberInputValue(
                                  dimension.price1m as number
                                )}
                                updatedValue={giveNumberInputValue(
                                  dimension.price1m as number
                                )}
                                isError={
                                  productDimensions.contractDuration?.includes(
                                    '1m'
                                  ) &&
                                  getIsError(`dimensions.${[index]}.price1m`)
                                }
                                onChange={e =>
                                  handleChangePrice(
                                    e,
                                    `productDimensions.dimensions.${index}.price1m`
                                  )
                                }
                                isReadOnly={
                                  !productDimensions.contractDuration?.includes(
                                    '1m'
                                  ) || (isFormLocked as boolean)
                                }
                                isRequired={false}
                                errorText={
                                  (productDimensions.contractDuration?.includes(
                                    '1m'
                                  ) &&
                                    getErrorText(
                                      `dimensions.${[index]}.price1m`
                                    )) ||
                                  ''
                                }
                                keyToPrevent={['+', '-', 'e']}
                                showSpinner={false}
                                onBlur={handleBlur}
                                viewOnly={detailView}
                              />
                            </div>
                          </Grid>
                          <Grid size={3}>
                            <div key={`contract-${randomKey}-price12m`}>
                              <TextInputV2
                                keyName={`productDimensions.dimensions.${index}.price12m`}
                                title="12 months price"
                                type="number"
                                placeHolder="12 months price"
                                value={giveNumberInputValue(
                                  dimension.price12m as number
                                )}
                                updatedValue={giveNumberInputValue(
                                  dimension.price12m as number
                                )}
                                isError={
                                  productDimensions.contractDuration?.includes(
                                    '12m'
                                  ) &&
                                  getIsError(`dimensions.${[index]}.price12m`)
                                }
                                onChange={e =>
                                  handleChangePrice(
                                    e,
                                    `productDimensions.dimensions.${index}.price12m`
                                  )
                                }
                                isReadOnly={
                                  !productDimensions.contractDuration?.includes(
                                    '12m'
                                  ) || (isFormLocked as boolean)
                                }
                                isRequired={false}
                                errorText={
                                  (productDimensions.contractDuration?.includes(
                                    '12m'
                                  ) &&
                                    getErrorText(
                                      `dimensions.${[index]}.price12m`
                                    )) ||
                                  ''
                                }
                                showSpinner={false}
                                onBlur={handleBlur}
                                keyToPrevent={['+', '-', 'e']}
                                viewOnly={detailView}
                              />
                            </div>
                          </Grid>
                          <Grid size={3}>
                            <div key={`contract-${randomKey}-price24m`}>
                              <TextInputV2
                                keyName={`productDimensions.dimensions.${index}.price24m`}
                                title="24 months price"
                                type="number"
                                placeHolder="24 months price"
                                value={giveNumberInputValue(
                                  dimension.price24m as number
                                )}
                                updatedValue={giveNumberInputValue(
                                  dimension.price24m as number
                                )}
                                isError={
                                  productDimensions.contractDuration?.includes(
                                    '24m'
                                  ) &&
                                  getIsError(`dimensions.${[index]}.price24m`)
                                }
                                onChange={e =>
                                  handleChangePrice(
                                    e,
                                    `productDimensions.dimensions.${index}.price24m`
                                  )
                                }
                                isReadOnly={
                                  !productDimensions.contractDuration?.includes(
                                    '24m'
                                  ) || (isFormLocked as boolean)
                                }
                                isRequired={false}
                                errorText={
                                  (productDimensions.contractDuration?.includes(
                                    '24m'
                                  ) &&
                                    getErrorText(
                                      `dimensions.${[index]}.price24m`
                                    )) ||
                                  ''
                                }
                                showSpinner={false}
                                onBlur={handleBlur}
                                keyToPrevent={['+', '-', 'e']}
                                viewOnly={detailView}
                              />
                            </div>
                          </Grid>
                          <Grid size={3}>
                            <div key={`contract-${randomKey}-price36m`}>
                              <TextInputV2
                                keyName={`productDimensions.dimensions.${index}.price36m`}
                                title="36 months price"
                                type="number"
                                placeHolder="36 months price"
                                value={giveNumberInputValue(
                                  dimension.price36m as number
                                )}
                                updatedValue={giveNumberInputValue(
                                  dimension.price36m as number
                                )}
                                isError={
                                  productDimensions.contractDuration?.includes(
                                    '36m'
                                  ) &&
                                  getIsError(`dimensions.${[index]}.price36m`)
                                }
                                onChange={e =>
                                  handleChangePrice(
                                    e,
                                    `productDimensions.dimensions.${index}.price36m`
                                  )
                                }
                                isReadOnly={
                                  !productDimensions.contractDuration?.includes(
                                    '36m'
                                  ) || (isFormLocked as boolean)
                                }
                                isRequired={false}
                                errorText={
                                  (productDimensions.contractDuration?.includes(
                                    '36m'
                                  ) &&
                                    getErrorText(
                                      `dimensions.${[index]}.price36m`
                                    )) ||
                                  ''
                                }
                                showSpinner={false}
                                onBlur={handleBlur}
                                keyToPrevent={['+', '-', 'e']}
                                viewOnly={detailView}
                              />
                            </div>
                          </Grid>
                        </Grid>
                      </LabraToolTipV2>
                    </div>

                    <Grid container>
                      <Grid className={classes.descriptionBottom} size={12}>
                        <Typography
                          className={clsx(
                            classes.sectionHeading,
                            classes.headingWithDesc
                          )}
                        >
                          Description
                        </Typography>
                        <Typography className={classes.headingDescription}>
                          This is the description of your plan that helps your
                          buyers understand its offerings.
                        </Typography>
                      </Grid>
                      <Grid size={12}>
                        <LabraToolTipV2
                          followCursor={detailView}
                          tooltipPlacement="bottom-start"
                          description={detailViewTooltip}
                        >
                          <div
                            key={`contract-${randomKey}-labelDescription`}
                            className={classes.sectionBottom}
                          >
                            <TextInputV2
                              keyName={`productDimensions.dimensions.${index}.labelDescription`}
                              title="Enter a brief description"
                              type="text"
                              placeHolder="Enter a brief description"
                              maxCharacters={1000}
                              value={dimension.labelDescription}
                              updatedValue={dimension.labelDescription}
                              isError={getIsError(
                                `dimensions.${[index]}.labelDescription`
                              )}
                              onChange={event =>
                                handleChangeDescription(event, index)
                              }
                              isReadOnly={isFormLocked as boolean}
                              isRequired={false}
                              errorText={getErrorText(
                                `dimensions.${[index]}.labelDescription`
                              )}
                              showSpinner={false}
                              onBlur={handleBlur}
                              supportingText="Eg: This plan allows you unlimited features like xyz."
                              viewOnly={detailView}
                              highlights={
                                listingValidation?.value &&
                                getIsError(
                                  `dimensions.${[index]}.labelDescription`
                                )
                                  ? getListingTextHighlights(
                                      dimension.labelDescription,
                                      'plain'
                                    )
                                  : []
                              }
                            />
                          </div>
                        </LabraToolTipV2>
                        <LabraToolTipV2
                          followCursor={
                            detailView ||
                            (productView && isAdditionalDimentionsEmpty)
                          }
                          tooltipPlacement="bottom-start"
                          description={
                            productView && isAdditionalDimentionsEmpty
                              ? notEditableContractDimensionsCheckboxTooltip
                              : detailViewTooltip
                          }
                        >
                          <div
                            key={`contract.${index}.checkBoxAdditionalUsageAllowed-${randomKeyCheckBox}`}
                          >
                            <CheckBoxFieldv2
                              key={`contract.${index}.checkBoxAdditionalUsageAllowed-${randomKeyCheckBox}`}
                              keyName={`productDimensions.dimensions.${index}.checkBoxAdditionalUsageAllowed`}
                              title={
                                <div>
                                  <Typography className={classes.checkboxTitle}>
                                    Activate pay-as-you-go pricing for
                                    additional usage on this contract dimension
                                  </Typography>
                                  <Typography
                                    className={classes.checkboxSubtitle}
                                  >
                                    This allows your users to pay-as-they-go for
                                    additional usage of this contract dimension
                                    if they have purchased it.This adds a
                                    corresponding usage dimension below with the
                                    same Display name in the{' '}
                                    <span className={classes.highLightedText}>
                                      Usage dimensions{' '}
                                    </span>
                                    section below.
                                  </Typography>
                                </div>
                              }
                              onChangeBox={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleCheckboxChange(
                                  event.target.checked,
                                  index,
                                  dimension as unknown as ContractDimension
                                )
                              }
                              value={!!dimension.checkBoxAdditionalUsageAllowed}
                              disabled={
                                isEmpty(dimension.label) ||
                                isFormLocked ||
                                detailView ||
                                (productView && isAdditionalDimentionsEmpty)
                              }
                            />
                          </div>
                        </LabraToolTipV2>
                      </Grid>
                    </Grid>
                  </div>
                ))}
                {dimensionLength <= 23 ? (
                  <>
                    <LabraToolTipV2
                      followCursor={detailView}
                      tooltipPlacement="bottom-start"
                      description={detailViewTooltip}
                      className={detailView ? classes.buttonWrapper : ''}
                    >
                      <ButtonV2
                        styleType="link"
                        onClick={() =>
                          push({
                            ...emptyContractDimension,
                            dimensionUnit: dimensionCategoryName,
                          })
                        }
                        data-testid="add-a-contractDimensions-button"
                        disabled={isDisable}
                      >
                        Add a contract dimension
                      </ButtonV2>
                      <Typography className={classes.buttonSubText}>
                        You can add {24 - dimensionLength} more contract
                        dimensions.
                      </Typography>
                    </LabraToolTipV2>
                  </>
                ) : (
                  <></>
                )}
              </>
            )}
          </FieldArray>
        </>
      }
    />
  )
}
